import { useCallback, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { TermsBanner } from './TermsBanner'
import { SupportSessionBanner } from './SupportSessionBanner'
import { SupportSessionBlockedBoundary } from './SupportSessionBlocked'
import { CommandPalette } from '@/components/search/CommandPalette'
import { ToastContainer } from '@/components/ui'
import { AssistantBubble } from '@/components/assistant/AssistantBubble'
import { useUIStore, useSearchStore } from '@/stores'
import { track } from '@/lib/analytics'
import { useMenuEdgeSwipe } from '@/hooks/useMenuEdgeSwipe'
import { useSupportSession } from '@/hooks/useSupportSession'

export interface MainLayoutProps {
  children: ReactNode
  headerActions?: ReactNode
}

export function MainLayout({ children, headerActions }: MainLayoutProps) {
  const { sidebarCollapsed, toggleSidebar, mobileSidebarOpen, setMobileSidebarOpen, pagePaintsBackground } = useUIStore()
  const toggleSearch = useSearchStore((s) => s.toggle)
  // EP-160: FlowRent support working in this account (browser tab only).
  const supportSession = useSupportSession().active

  // iOS app: a left-edge swipe on a page the menu links to opens the menu
  // instead of swiping back (owner, 2026-09-29). See useMenuEdgeSwipe.
  const mobileDrawerRef = useRef<HTMLDivElement>(null)
  const openMobileMenu = useCallback(() => setMobileSidebarOpen(true), [setMobileSidebarOpen])
  useMenuEdgeSwipe({ drawerRef: mobileDrawerRef, open: mobileSidebarOpen, onOpen: openMobileMenu })

  // Global Cmd+K listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        toggleSearch()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggleSearch])

  // EP-73 — coach milestone: report sub-pages have no shared shell, so the
  // layout is the one central anchor. Depth ≥ 2 under /reports = an actual
  // report leaf (/reports and the group pages don't count as "viewed").
  const { pathname } = useLocation()
  useEffect(() => {
    const match = pathname.match(/^\/reports\/([^/]+)\/(.+)$/)
    if (match) {
      track('report_viewed', { report: `${match[1]}/${match[2]}` })
    }
  }, [pathname])

  return (
    // EP-160 S3: a support tab's banner sits in flow above the sidebar and the
    // page column, so it never covers the page or its bottom action bar.
    <div className="flex flex-col h-screen bg-base-200">
      <SupportSessionBanner />
      <div className="flex flex-1 min-h-0">
        {/* Desktop Sidebar */}
        {/* max-h-full: the sidebar is h-dvh, which is taller than this row
            when a support session's banner sits above it. */}
        <div className="hidden lg:block">
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
            className="max-h-full"
          />
        </div>

        {/* Mobile Sidebar backdrop. The mobile drawer is a FULL-SCREEN white
            overlay, so there is nothing behind it to dim — a dark backdrop only
            leaked as gray strips in the iOS Safari safe-areas (status bar + bottom
            toolbar), where the transformed drawer's own white bg doesn't paint but
            this simple fixed element's background-color does. Paint it white
            (bg-base-100) so those strips match the drawer → the whole screen is
            white while the menu is open. Still tap-to-close. Box matches the drawer
            (top-0 h-dvh, not inset-0, which on iOS resolves to the taller layout
            viewport and would peek past the drawer). */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-x-0 top-0 h-dvh z-40 bg-base-100 lg:hidden pointer-events-auto"
            aria-hidden="true"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* Mobile Sidebar - Fullscreen.
            B-022: inset the horizontal safe areas (notch / rounded corners /
            dynamic island in landscape) so the drawer content never renders under
            them, and overflow-hidden so a child can never paint outside the panel
            (the cause of the clipped 'owRent' wordmark / clipped Thai leading
            chars). Top safe area is handled inside Sidebar's logo row.
            Height is `top-0 h-dvh` (dynamic viewport), NOT `inset-0` (bottom:0):
            on iOS Safari the layout-viewport bottom sits behind the address bar,
            so `bottom:0` both hid the drawer footer and left the full-height dark
            backdrop peeking past the white panel. `h-dvh` tracks the visible
            viewport so the panel exactly covers what the user sees. */}
        <div
          ref={mobileDrawerRef}
          className={`fixed inset-x-0 top-0 h-dvh z-50 lg:hidden bg-base-100 overflow-hidden transform transition-transform duration-300 pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] ${
            mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
          }`}
          aria-hidden={!mobileSidebarOpen}
        >
          <Sidebar mobile onClose={() => setMobileSidebarOpen(false)} />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          <Header
            onMenuClick={() => setMobileSidebarOpen(true)}
            actions={headerActions}
          />
          {/* Mobile gets extra top breathing room below the app bar (pt-6);
              desktop unchanged via lg:p-6. Bottom keeps the safe-area inset with a
              1rem floor so it's never flush on non-notch devices. */}
          {/* Bottom padding clears the safe area AND the PageHeaderActions bottom
              action bar, whose measured height it publishes as
              --page-action-bar-height (0px when no bar, including at sm and up).
              This replaces an in-flow spacer that PageHeaderActions used to render
              — but PageHeaderActions lives in the page HEADER, so that spacer
              opened a blank band under the title instead of clearing the bar. */}
          {/* pagePaintsBackground: a page that paints its own grey (Settings)
              gets a <main> with no padding below lg and carries that padding
              itself. <main> is grey on its top half and white on its bottom half,
              which only shows when iOS bounces the page: a pull at the top shows
              grey, like the page, and a pull at the bottom shows white, like the
              save bar there (owner, 2026-09-30). */}
          <main
            className={`flex-1 overflow-auto lg:p-6 ${
              pagePaintsBackground
                ? 'bg-linear-to-b from-base-200 from-50% to-base-100 to-50% lg:bg-none'
                : 'px-4 pt-6 pb-[calc(max(env(safe-area-inset-bottom),1rem)+var(--page-action-bar-height,0px))]'
            }`}
          >
            {/* EP-160 D5: the owner's one-time terms banner, above every page.
                A page that paints its own background has no <main> padding
                below lg, so the banner carries that padding itself there. */}
            {/* Not in a support session: accepting the terms is the owner's. */}
            {!supportSession && (
              <TermsBanner className={pagePaintsBackground ? 'mx-4 mt-6 lg:mx-0 lg:mt-0 lg:mb-6' : 'mb-6'} />
            )}
            {/* EP-160 S3: a page the session may not open shows the blocked
                page instead (403 support_session_blocked thrown by its query). */}
            <SupportSessionBlockedBoundary key={pathname}>{children}</SupportSessionBlockedBoundary>
          </main>
        </div>
      </div>

      {/* Command Palette */}
      <CommandPalette />

      {/* Toast Notifications */}
      <ToastContainer />

      {/* EP-73 — AI feature coach (dual-gated + once/day; self-hides) */}
      <AssistantBubble />
    </div>
  )
}
